import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import api from '../config/api';
import { DOCUMENT } from '../constants/documentTheme';

const POLL_INTERVAL_MS = 10000;

const partnerAuthConfig = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('partnerToken')}` },
});

/** Message times arrive as UTC "YYYY-MM-DD HH:mm:ss" or ISO strings. */
const parseMessageTime = (value) => {
  if (!value) return null;
  const text = String(value);
  const iso = /[zZ]|[+-]\d{2}:?\d{2}$/.test(text) ? text : `${text.replace(' ', 'T')}Z`;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * The group trip's chat as the organizer sees it. Partners are not on the
 * traveler websocket, so the thread refreshes on an interval while open;
 * travelers still receive the organizer's messages live.
 */
const PartnerGroupChat = ({ groupTripId, isActive = true }) => {
  const { t } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);

  const loadMessages = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const response = await api.get(`/partners/master-trips/${groupTripId}/messages`, partnerAuthConfig());
      if (!Array.isArray(response.data?.messages)) throw new Error('Unexpected chat response');
      setMessages(response.data.messages);
      setError('');
    } catch (err) {
      if (!quiet) setError(t('partnerManage.chatLoadFailed'));
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [groupTripId, t]);

  useEffect(() => {
    if (!isActive || !groupTripId) return undefined;
    loadMessages();
    const timer = setInterval(() => loadMessages({ quiet: true }), POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [isActive, groupTripId, loadMessages]);

  useEffect(() => {
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [messages.length]);

  const handleSend = async () => {
    const content = draft.trim();
    if (!content || sending) return;
    setSending(true);
    try {
      const response = await api.post(
        `/partners/master-trips/${groupTripId}/messages`,
        { content },
        partnerAuthConfig()
      );
      const sent = response.data?.message;
      if (sent?.id) {
        setMessages((prev) => (prev.some((m) => m.id === sent.id) ? prev : [...prev, sent]));
      }
      setDraft('');
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || t('partnerManage.chatSendFailed'));
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  return (
    <Box data-testid="partner-group-chat">
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('partnerManage.chatHint')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 0 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box
        ref={listRef}
        sx={{
          border: `1px solid ${DOCUMENT.rule}`,
          bgcolor: DOCUMENT.surface,
          height: { xs: 360, md: 460 },
          overflowY: 'auto',
          p: 2,
          mb: 2,
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : messages.length === 0 ? (
          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 6 }}>
            {t('partnerManage.chatEmpty')}
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {messages.map((message) => {
              const sentAt = parseMessageTime(message.created_at);
              const fromOrganizer = Boolean(message.from_organizer);
              return (
                <Box
                  key={message.id}
                  data-testid="partner-group-chat-message"
                  sx={{
                    alignSelf: fromOrganizer ? 'flex-end' : 'flex-start',
                    maxWidth: { xs: '90%', md: '70%' },
                    border: `1px solid ${fromOrganizer ? DOCUMENT.accent : DOCUMENT.rule}`,
                    bgcolor: fromOrganizer ? 'rgba(74,14,21,0.04)' : 'transparent',
                    px: 1.5,
                    py: 1,
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5, flexWrap: 'wrap' }}>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {message.sender_name}
                    </Typography>
                    {fromOrganizer && (
                      <Chip
                        label={t('partnerManage.chatOrganizer')}
                        size="small"
                        sx={{ height: 18, borderRadius: 0, fontSize: '0.625rem' }}
                      />
                    )}
                    {sentAt && (
                      <Typography variant="caption" color="text.secondary">
                        {format(sentAt, 'd MMM, HH:mm')}
                      </Typography>
                    )}
                  </Stack>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {message.content}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-end' }}>
        <TextField
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('partnerManage.chatPlaceholder')}
          multiline
          maxRows={4}
          fullWidth
          size="small"
          inputProps={{ maxLength: 1000, 'aria-label': t('partnerManage.chatPlaceholder') }}
        />
        <Button
          variant="contained"
          disableElevation
          startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
          onClick={handleSend}
          disabled={sending || !draft.trim()}
          sx={{ borderRadius: 0, bgcolor: DOCUMENT.oxblood, minWidth: 112 }}
        >
          {t('partnerManage.chatSend')}
        </Button>
      </Stack>
    </Box>
  );
};

export default PartnerGroupChat;
